// System Recovery — accessible only to is_system users.
//
// Three sections:
//   1. Status — current toggles + counts + last-action timestamps
//   2. Locks — turn lockdown / readonly on/off, force-logout everyone
//   3. User recovery — pick any user, promote / reactivate / reset password
//   4. Destructive — wipe project data (typed confirm)
//   5. Recovery commands — read-only docs for things that need shell access

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

type StateSnapshot = {
  lockdown_enabled: boolean;
  readonly_enabled: boolean;
  jwt_min_version: number;
  lockdown_set_at: string | null;
  readonly_set_at: string | null;
  last_force_logout_at: string | null;
  lockdown_set_by_email: string | null;
  readonly_set_by_email: string | null;
  last_force_logout_by_email: string | null;
  counts: { users_total: number; users_system: number; projects: number; segments: number; permits: number };
};

type AnyUser = {
  id: string; email: string; name: string | null; role: string;
  is_active: boolean; is_system: boolean; created_at: string;
};

const ROLES = ['admin', 'manager', 'field', 'viewer'] as const;

export default function SystemRecoveryPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: me } = useQuery<{ is_system?: boolean }>({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me'),
  });

  // Hard bounce — non-system users never see this page even if they brute the URL.
  useEffect(() => {
    if (me && !me.is_system) navigate('/dashboard', { replace: true });
  }, [me, navigate]);

  const { data: state, refetch: refetchState } = useQuery<StateSnapshot>({
    queryKey: ['system-state'],
    queryFn: () => api.get('/system/state'),
    enabled: !!me?.is_system,
    refetchInterval: 5_000,
  });

  const { data: usersData, refetch: refetchUsers } = useQuery<{ items: AnyUser[] }>({
    queryKey: ['system-users'],
    queryFn: () => api.get('/system/users'),
    enabled: !!me?.is_system,
  });

  const toggleLockdown = useMutation({
    mutationFn: (enabled: boolean) => api.post('/system/lockdown', { enabled }),
    onSuccess: () => refetchState(),
  });
  const toggleReadonly = useMutation({
    mutationFn: (enabled: boolean) => api.post('/system/readonly', { enabled }),
    onSuccess: () => refetchState(),
  });
  const forceLogout = useMutation({
    mutationFn: () => api.post('/system/force-logout', {}),
    onSuccess: () => {
      // We just invalidated our own session too. Bounce to login.
      setTimeout(() => navigate('/login', { replace: true }), 800);
    },
  });

  const [credToast, setCredToast] = useState<{ email: string; password: string } | null>(null);
  const resetMut = useMutation({
    mutationFn: (id: string) => api.post(`/system/users/${id}/reset-password`, {}) as Promise<any>,
    onSuccess: (data: any) => setCredToast({ email: data.email, password: data.temp_password }),
  });
  const promoteMut = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => api.post(`/system/users/${id}/promote`, { role }),
    onSuccess: () => refetchUsers(),
  });
  const reactivateMut = useMutation({
    mutationFn: (id: string) => api.post(`/system/users/${id}/reactivate`, {}),
    onSuccess: () => refetchUsers(),
  });

  const [wipeConfirm, setWipeConfirm] = useState('');
  const [wipeResult, setWipeResult] = useState<any>(null);
  const wipeMut = useMutation({
    mutationFn: () => api.post('/system/wipe-projects', { confirm: 'WIPE PROJECTS' }) as Promise<any>,
    onSuccess: (data: any) => {
      setWipeResult(data);
      setWipeConfirm('');
      refetchState();
    },
  });

  if (me && !me.is_system) return null;

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">🛡 System Recovery</h1>
          <div className="page__subtitle">
            Break-glass controls — visible only to system support accounts.
            Use carefully; some of these affect every user immediately.
          </div>
        </div>
      </div>

      {/* STATUS */}
      <div style={{
        background: state?.lockdown_enabled || state?.readonly_enabled ? '#fef3c7' : '#fff',
        border: `1px solid ${state?.lockdown_enabled || state?.readonly_enabled ? '#fbbf24' : 'var(--border)'}`,
        borderRadius: 6, padding: 16, marginBottom: 16,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
          System status
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          <Stat label="Lockdown" value={state?.lockdown_enabled ? 'ON' : 'off'} tone={state?.lockdown_enabled ? 'bad' : 'good'}
            sub={state?.lockdown_enabled ? `set by ${state.lockdown_set_by_email ?? '?'}` : 'open'} />
          <Stat label="Read-only" value={state?.readonly_enabled ? 'ON' : 'off'} tone={state?.readonly_enabled ? 'bad' : 'good'}
            sub={state?.readonly_enabled ? `set by ${state.readonly_set_by_email ?? '?'}` : 'writes ok'} />
          <Stat label="JWT version" value={String(state?.jwt_min_version ?? '—')} sub="bump = kick all" />
          <Stat label="Users" value={String(state?.counts?.users_total ?? '—')} sub={`${state?.counts?.users_system ?? 0} system`} />
          <Stat label="Projects / segs / permits" value={`${state?.counts?.projects ?? '?'} / ${state?.counts?.segments ?? '?'} / ${state?.counts?.permits ?? '?'}`} />
        </div>
      </div>

      {/* LOCKS */}
      <Section title="Locks" caption="Soft locks that take effect immediately. The cache TTL is 5s across processes.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Action
            label="Lockdown mode"
            sub="When ON, only system support accounts can log in. Also bumps the JWT version on enable, immediately invalidating every active non-system session."
            onValue={state?.lockdown_enabled ?? false}
            busy={toggleLockdown.isPending}
            onToggle={() => toggleLockdown.mutate(!state?.lockdown_enabled)}
          />
          <Action
            label="Read-only mode"
            sub="When ON, all writes from non-system users return 503. /auth/logout and own-password-change still work so users can sign themselves out cleanly."
            onValue={state?.readonly_enabled ?? false}
            busy={toggleReadonly.isPending}
            onToggle={() => toggleReadonly.mutate(!state?.readonly_enabled)}
          />
          <div style={{
            padding: 12, border: '1px solid #cbd5e1', borderRadius: 4, background: '#f8fafc',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Force-logout everyone</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, maxWidth: 600 }}>
                Bumps the JWT version. Every active session — including yours — becomes invalid on its next request.
                Use this if you suspect credential compromise, or after a sensitive role change.
              </div>
              {state?.last_force_logout_at && (
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                  Last: {new Date(state.last_force_logout_at).toLocaleString()} by {state.last_force_logout_by_email ?? '?'}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                if (confirm('Force-logout every active session, including yours. Continue?')) {
                  forceLogout.mutate();
                }
              }}
              disabled={forceLogout.isPending}
              style={{ ...btnDangerOutline, whiteSpace: 'nowrap' }}
            >
              {forceLogout.isPending ? 'Logging everyone out…' : '⚡ Force-logout all'}
            </button>
          </div>
        </div>
      </Section>

      {/* USER RECOVERY */}
      <Section title="User recovery" caption="System users (kevin@…) are NOT in this list — they're managed only via the provisioner script.">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <th style={th}>Email</th>
              <th style={th}>Role</th>
              <th style={th}>Active</th>
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(usersData?.items ?? []).filter(u => !u.is_system).map(u => (
              <tr key={u.id} style={{ borderTop: '1px solid #f1f5f9', opacity: u.is_active ? 1 : 0.5 }}>
                <td style={td}><b>{u.email}</b>{u.name && <span style={{ color: '#64748b', marginLeft: 6, fontSize: 11 }}>· {u.name}</span>}</td>
                <td style={td}>
                  <select value={u.role}
                    disabled={promoteMut.isPending}
                    onChange={e => promoteMut.mutate({ id: u.id, role: e.target.value })}
                    style={{ fontSize: 11, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 3 }}>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td style={td}>
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 999, fontWeight: 700,
                    background: u.is_active ? '#dcfce7' : '#e2e8f0',
                    color:      u.is_active ? '#166534' : '#475569',
                  }}>{u.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button onClick={() => resetMut.mutate(u.id)} disabled={resetMut.isPending} style={btnSecondary}>
                    Reset password
                  </button>
                  {!u.is_active && (
                    <button onClick={() => reactivateMut.mutate(u.id)} style={{ ...btnSecondary, color: '#166534', marginLeft: 4 }}>
                      Reactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {/* System users — display only, no actions */}
            {(usersData?.items ?? []).filter(u => u.is_system).length > 0 && (
              <tr><td colSpan={4} style={{ padding: '14px 12px 4px', fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', background: '#f8fafc' }}>
                System support accounts (managed via provisioner)
              </td></tr>
            )}
            {(usersData?.items ?? []).filter(u => u.is_system).map(u => (
              <tr key={u.id} style={{ borderTop: '1px solid #f1f5f9', background: '#fefce8' }}>
                <td style={td}><b>🛡 {u.email}</b>{u.name && <span style={{ color: '#64748b', marginLeft: 6, fontSize: 11 }}>· {u.name}</span>}</td>
                <td style={td}><i style={{ color: '#475569', fontSize: 11 }}>{u.role}</i></td>
                <td style={td}><i style={{ color: '#475569', fontSize: 11 }}>system</i></td>
                <td style={{ ...td, textAlign: 'right', fontSize: 10, color: '#92400e' }}>db-locked · use provisioner</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {/* DESTRUCTIVE */}
      <Section title="Destructive — wipe project data" caption="Deletes every project, permit, segment, draw, obligation row. Preserves users, agencies, obligation catalog, plant settings. Use for decommissioning or transferring the droplet.">
        <div style={{
          padding: 14, border: '2px solid #fca5a5', borderRadius: 6, background: '#fef2f2',
        }}>
          <div style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 10 }}>
            Type <code style={{ background: '#fee2e2', padding: '1px 5px', borderRadius: 2, fontFamily: 'ui-monospace, monospace' }}>WIPE PROJECTS</code> to enable the button.
          </div>
          <input value={wipeConfirm} onChange={e => setWipeConfirm(e.target.value)}
            placeholder='Type "WIPE PROJECTS" exactly'
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3, marginBottom: 10 }} />
          <button
            onClick={() => {
              if (wipeConfirm === 'WIPE PROJECTS' && confirm('Delete EVERY project, permit, segment, draw, and obligation? This is irreversible.')) {
                wipeMut.mutate();
              }
            }}
            disabled={wipeConfirm !== 'WIPE PROJECTS' || wipeMut.isPending}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 700,
              background: wipeConfirm === 'WIPE PROJECTS' ? '#dc2626' : '#cbd5e1',
              color: '#fff', border: 0, borderRadius: 4,
              cursor: wipeConfirm === 'WIPE PROJECTS' ? 'pointer' : 'not-allowed',
            }}
          >
            {wipeMut.isPending ? 'Wiping…' : '☠ Wipe project data'}
          </button>
          {wipeResult && (
            <div style={{ marginTop: 12, padding: 10, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Wipe complete. Rows deleted per table:</div>
              {Object.entries(wipeResult.deleted ?? {}).map(([t, n]) => (
                <div key={t} style={{ color: (n as number) === -1 ? '#94a3b8' : '#0f172a' }}>
                  {t}: {(n as number) === -1 ? '(table not present)' : (n as number)}
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* RECOVERY COMMANDS (docs) */}
      <Section title="Out-of-band recovery commands" caption="These need shell access on the droplet. Copy and run via your existing ops channel — they're not executed from this page (and they couldn't be on a stolen instance anyway).">
        <CmdBlock title="Rotate the system user's password" cmd={`cd /opt/fiberforge/backend
set -a && . /opt/fiberforge/.env && set +a
node _provision_system_user.mjs kevin@securenetfiber.com '<new-password>'`} />
        <CmdBlock title="Kill the fastify process (hard stop)" cmd={`sudo systemctl stop fiberforge-api`} />
        <CmdBlock title="Disable fastify from auto-starting on boot" cmd={`sudo systemctl disable fiberforge-api`} />
        <CmdBlock title="Drop the protection trigger (only if you need to manually edit the system user row)" cmd={`sudo -u postgres psql -d fiberforge -c "DROP TRIGGER trg_protect_system_users ON users;"`} />
        <CmdBlock title="Re-apply the protection trigger" cmd={`sudo -u postgres psql -d fiberforge -f /opt/fiberforge/db/migrations/_migration_lock_system_users.sql`} />
      </Section>

      {credToast && <CredsModal email={credToast.email} password={credToast.password} onClose={() => setCredToast(null)} />}
    </>
  );
}

function Stat({ label, value, tone, sub }: { label: string; value: string; tone?: 'good' | 'bad'; sub?: string }) {
  const color = tone === 'bad' ? '#dc2626' : tone === 'good' ? '#16a34a' : '#0f172a';
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, caption, children }: { title: string; caption?: string; children: any }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 6, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: caption ? 2 : 10 }}>{title}</div>
      {caption && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>{caption}</div>}
      <div>{children}</div>
    </div>
  );
}

function Action({ label, sub, onValue, busy, onToggle }: { label: string; sub: string; onValue: boolean; busy: boolean; onToggle: () => void }) {
  return (
    <div style={{
      padding: 12, border: '1px solid #cbd5e1', borderRadius: 4,
      background: onValue ? '#fef3c7' : '#f8fafc',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{label} <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 8px', borderRadius: 999, background: onValue ? '#dc2626' : '#e2e8f0', color: onValue ? '#fff' : '#475569' }}>{onValue ? 'ON' : 'off'}</span></div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, maxWidth: 600 }}>{sub}</div>
      </div>
      <button onClick={() => {
        if (confirm(`${onValue ? 'Disable' : 'Enable'} ${label}?`)) onToggle();
      }} disabled={busy} style={{
        padding: '6px 14px', fontSize: 12, fontWeight: 700,
        background: onValue ? '#10b981' : '#dc2626', color: '#fff',
        border: 0, borderRadius: 4, cursor: busy ? 'wait' : 'pointer',
        whiteSpace: 'nowrap',
      }}>
        {busy ? '…' : (onValue ? 'Disable' : 'Enable')}
      </button>
    </div>
  );
}

function CmdBlock({ title, cmd }: { title: string; cmd: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{title}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
        <pre style={{
          flex: 1, margin: 0, padding: '8px 12px', background: '#0f172a', color: '#e2e8f0',
          fontSize: 11, fontFamily: 'ui-monospace, monospace', borderRadius: 3, overflow: 'auto',
          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>{cmd}</pre>
        <button onClick={() => {
          navigator.clipboard?.writeText(cmd).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
        }} style={{
          padding: '0 10px', fontSize: 11, fontWeight: 600,
          background: copied ? '#10b981' : '#1e3a8a', color: '#fff',
          border: 0, borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
        }}>{copied ? '✓' : 'Copy'}</button>
      </div>
    </div>
  );
}

function CredsModal({ email, password, onClose }: { email: string; password: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, maxWidth: '95%', background: '#fff', borderRadius: 8, padding: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>One-time password</div>
        <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
          Send to <b>{email}</b> via your trusted channel.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <code style={{ flex: 1, padding: '10px 14px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 16, fontFamily: 'ui-monospace, monospace' }}>
            {password}
          </code>
          <button onClick={() => {
            navigator.clipboard?.writeText(password).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
          }} style={{
            padding: '10px 14px', fontSize: 13, fontWeight: 600,
            background: copied ? '#10b981' : '#1e3a8a', color: '#fff',
            border: 0, borderRadius: 4, cursor: 'pointer',
          }}>{copied ? '✓ Copied' : 'Copy'}</button>
        </div>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', fontSize: 13, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' };
const td: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const btnSecondary: React.CSSProperties = { padding: '5px 10px', fontSize: 11, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' };
const btnDangerOutline: React.CSSProperties = { padding: '6px 14px', fontSize: 12, fontWeight: 700, background: '#fff', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 4, cursor: 'pointer' };
